/**
 * useEntityRefresh — sincroniza listas entre pestañas del mismo origen.
 *
 * USO en la pestaña madre (PeticionDetail, presupuesto…) que muestra un combobox:
 *   useEntityRefresh("espacio", () => loadEspacios());
 *
 * USO en la pestaña hija (Espacios, Clientes…) cuando crea o edita:
 *   broadcastEntityChange("espacio");
 *
 * Soporta cualquier entidad: "cliente", "espacio", "cadena", "subcliente",
 * "proveedor", "colaborador", "zona".
 *
 * Implementación: usa `BroadcastChannel` (todos los navegadores modernos)
 * con fallback a `localStorage` events para Safari < 15.4.
 */

import { useEffect, useRef } from "react";

const CHANNEL_NAME = "dnf-entity-changes";

/** Notifica al resto de pestañas que la entidad cambió (creada/editada/borrada). */
export function broadcastEntityChange(entityType, metadata = {}) {
  const payload = { entityType, ts: Date.now(), ...metadata };
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const ch = new BroadcastChannel(CHANNEL_NAME);
      ch.postMessage(payload);
      ch.close();
    }
  } catch { /* ignore */ }
  // Fallback localStorage para navegadores sin BroadcastChannel
  try {
    localStorage.setItem(`${CHANNEL_NAME}:${entityType}`, JSON.stringify(payload));
    // Inmediatamente borramos para permitir disparar de nuevo el mismo type
    setTimeout(() => {
      try { localStorage.removeItem(`${CHANNEL_NAME}:${entityType}`); } catch { /* */ }
    }, 100);
  } catch { /* ignore */ }
}

/** Hook que recarga la lista cuando otra pestaña notifica un cambio. */
export function useEntityRefresh(entityType, onChange) {
  const handlerRef = useRef(onChange);
  handlerRef.current = onChange;
  useEffect(() => {
    let bc = null;
    const onMsg = (e) => {
      const data = e.data || e;
      if (!data || data.entityType !== entityType) return;
      // Debounce mínimo para evitar múltiples refrescos por bursts
      handlerRef.current?.(data);
    };
    if (typeof BroadcastChannel !== "undefined") {
      bc = new BroadcastChannel(CHANNEL_NAME);
      bc.addEventListener("message", onMsg);
    }
    const onStorage = (e) => {
      if (!e.key || !e.key.startsWith(`${CHANNEL_NAME}:`)) return;
      if (!e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue);
        if (parsed?.entityType === entityType) handlerRef.current?.(parsed);
      } catch { /* */ }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      try { bc?.close(); } catch { /* */ }
      window.removeEventListener("storage", onStorage);
    };
  }, [entityType]);
}
